import React, { useMemo } from 'react';
import { Dropdown } from 'primereact/dropdown';
import { FactCostEnum, FactMetadataOutDTO } from '@api/ringil-api.v2';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import { factGroupLabel } from '../../i18nKeys';

interface ComponentProps {
  facts: FactMetadataOutDTO[];
  value: string;
  onChange: (factKey: string) => void;
  disabled?: boolean;
}

interface FactOption {
  label: string;
  value: string;
  fact: FactMetadataOutDTO;
}

/**
 * Picks a fact, grouped by the catalogue's own `group`. The list comes from the metadata endpoint
 * already filtered to the selected trigger, so a fact the trigger cannot provide is simply not
 * offered — the backend would refuse the rule at save time, and finding that out after typing it
 * is a poor experience.
 */
const FactPicker: React.FC<ComponentProps> = ({ facts, value, onChange, disabled }) => {
  const { tr } = useTranslationLgs();

  const groups = useMemo(() => {
    const byGroup = new Map<string, FactOption[]>();
    facts.forEach(fact => {
      const options = byGroup.get(fact.group) ?? [];
      options.push({ label: tr(fact.i18nKey, fact.key), value: fact.key, fact });
      byGroup.set(fact.group, options);
    });
    return Array.from(byGroup.entries())
      .sort(([left], [right]) => left.localeCompare(right))
      .map(([group, options]) => ({
        // The catalogue's `group` is a bare string with no i18nKey of its own — see factGroupLabel.
        label: factGroupLabel(tr, group),
        items: options.sort((left, right) => left.label.localeCompare(right.label)),
      }));
  }, [facts, tr]);

  const optionTemplate = (option: FactOption) => (
    <div className="flex items-center justify-between gap-3">
      <span>{option.label}</span>
      <span className="flex items-center gap-2">
        <span className="font-mono text-xs text-content-tertiary">{option.value}</span>
        {/* The only hint the author gets that a condition costs a query — see FactCost in Facts.kt. */}
        {option.fact.factCost === FactCostEnum.EXPENSIVE && (
          <span
            className="rounded bg-base-tertiary px-1.5 py-0.5 text-xs text-content-tertiary"
            title={tr('AiDispatcher.expensiveFact', 'Resolving this fact costs a database query')}
          >
            {tr('AiDispatcher.expensiveFactShort', 'slow')}
          </span>
        )}
      </span>
    </div>
  );

  return (
    <Dropdown
      className="w-full"
      value={value || null}
      options={groups}
      optionGroupLabel="label"
      optionGroupChildren="items"
      itemTemplate={optionTemplate}
      onChange={event => onChange(event.value)}
      disabled={disabled}
      filter
      filterBy="label,value"
      placeholder={tr('AiDispatcher.selectFact', 'Select a fact')}
    />
  );
};

export default FactPicker;
